import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rptService } from '../../../../services/rptService';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  InputAdornment,
  Tooltip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Assessment as AssessmentIcon,
  MonetizationOn as MonetizationOnIcon,
  AccountBalance as AccountBalanceIcon,
  Home as HomeIcon,
  Mail as MailIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const ReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [breakdownData, setBreakdownData] = useState(null);
  const [amnestySettings, setAmnestySettings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [barangayFilter, setBarangayFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('delinquent');
  const [barangays, setBarangays] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    loadProperties();
  }, [statusFilter]);

  const loadProperties = async () => {
    setLoading(true);
    try {
      // Load properties, amnesty settings, and barangays in parallel
      const [propsResult, amnestyResult, barangaysResult] = await Promise.all([
        rptService.getDelinquentProperties(statusFilter),
        rptService.getAllAmnesty(),
        rptService.getAllBarangays()
      ]);

      if (propsResult.success) {
        setProperties(propsResult.data);
      } else {
        setError(propsResult.error || 'Failed to load report data');
      }

      if (amnestyResult.success) {
        setAmnestySettings(amnestyResult.data);
      }

      if (barangaysResult.success) {
        setBarangays(barangaysResult.data);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₱0.00';
    return `₱${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (prop, calc) => {
    setSelectedProperty(prop);
    setBreakdownData(calc);
  };

  const handleCloseModal = () => {
    setSelectedProperty(null);
    setBreakdownData(null);
  };

  const filteredProperties = React.useMemo(() => {
    return properties.filter(prop => {
      const matchesSearch = searchTerm === '' || 
        (prop.owner_name && prop.owner_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (prop.td_number && prop.td_number.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesBarangay = barangayFilter === '' || prop.barangay === barangayFilter;

      return matchesSearch && matchesBarangay;
    });
  }, [properties, searchTerm, barangayFilter]);

  const calculateDelinquency = (prop) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    // Start year is last_payment_year + 1
    // If no last_payment_year, default to effectivity_year or reasonable default
    const startYear = (prop.last_payment_year || (prop.effectivity_year ? prop.effectivity_year - 1 : currentYear - 5)) + 1;
    
    // Include current year to show amounts for "For Payment" status and full liability
    // For Delinquency Report, exclude current year as requested
    const endYear = statusFilter === 'delinquent' ? currentYear - 1 : currentYear;

    let totalBasicTax = 0;
    let totalBasicPenalty = 0;
    let totalSefTax = 0;
    let totalSefPenalty = 0;
    const breakdown = [];

    const av = parseFloat(prop.assessed_value || 0);

    for (let year = startYear; year <= endYear; year++) {
      // Basic Tax (1%)
      const basicTax = av * 0.01;
      // SEF Tax (1%)
      const sefTax = av * 0.01;

      // Penalty Calculation
      let penaltyPercent = 0;
      let isAmnesty = false;

      // Check for Amnesty first
      const applicableAmnesty = amnestySettings.find(a => year >= a.tax_year_from && year <= a.tax_year_to);
      
      if (applicableAmnesty) {
        penaltyPercent = applicableAmnesty.penalty_percent;
        isAmnesty = true;
      } else {
        if (year === currentYear) {
          // Current Year Logic
          if (currentMonth >= 1 && currentMonth <= 3) {
            // Early Payment - No Penalty (Discount logic ignored for reports for now)
            penaltyPercent = 0;
          } else if (currentMonth >= 4) {
            // Penalty starting April
            const monthsSinceApril = currentMonth - 4 + 1;
            penaltyPercent = Math.min(8 + (monthsSinceApril - 1) * 2, 72);
          }
        } else {
          // Standard Penalty Calculation (Past Years)
          const yearsPast = currentYear - year;
          
          // Months logic from TaxCalculator.js
          let monthsFromTaxYear = 9;
          let monthsFromCompleteYears = (yearsPast - 1) * 12;
          let monthsInCurrentYear = currentMonth;
          
          const totalPenaltyMonths = monthsFromTaxYear + monthsFromCompleteYears + monthsInCurrentYear;
          penaltyPercent = Math.min(8 + (totalPenaltyMonths - 1) * 2, 72);
        }
      }

      const basicPenalty = basicTax * (penaltyPercent / 100);
      const sefPenalty = sefTax * (penaltyPercent / 100);

      const yearTotal = basicTax + basicPenalty + sefTax + sefPenalty;

      breakdown.push({
        year,
        basicTax,
        basicPenalty,
        sefTax,
        sefPenalty,
        total: yearTotal,
        isAmnesty,
        penaltyPercent
      });

      totalBasicTax += basicTax;
      totalBasicPenalty += basicPenalty;
      totalSefTax += sefTax;
      totalSefPenalty += sefPenalty;
    }

    const annualTax = av * 0.02; // 2% total
    const basicSubtotal = totalBasicTax + totalBasicPenalty;
    const sefSubtotal = totalSefTax + totalSefPenalty;
    const grandTotal = basicSubtotal + sefSubtotal;

    return {
      startYear,
      endYear,
      annualTax,
      basicTax: totalBasicTax,
      basicPenalty: totalBasicPenalty,
      basicSubtotal,
      sefTax: totalSefTax,
      sefPenalty: totalSefPenalty,
      sefSubtotal,
      grandTotal,
      breakdown
    };
  };

  const processedProperties = React.useMemo(() => {
    return filteredProperties.map(prop => ({
      ...prop,
      calculation: calculateDelinquency(prop)
    }));
  }, [filteredProperties, amnestySettings, statusFilter]);

  const sortedProperties = React.useMemo(() => {
    let sortableItems = [...processedProperties];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;
        
        if (['annualTax', 'startYear', 'endYear', 'grandTotal'].includes(sortConfig.key)) {
           aValue = a.calculation[sortConfig.key];
           bValue = b.calculation[sortConfig.key];
        } else if (sortConfig.key === 'assessed_value') {
           aValue = parseFloat(a.assessed_value || 0);
           bValue = parseFloat(b.assessed_value || 0);
        } else {
           aValue = a[sortConfig.key] ? a[sortConfig.key].toString().toLowerCase() : '';
           bValue = b[sortConfig.key] ? b[sortConfig.key].toString().toLowerCase() : '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [processedProperties, sortConfig]);

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    let totalAmount = 0;
    let totalBasic = 0;
    let totalSef = 0;
    let totalPenalty = 0;

    processedProperties.forEach(prop => {
      const calc = prop.calculation;
      totalAmount += calc.grandTotal;
      totalBasic += calc.basicTax;
      totalSef += calc.sefTax;
      totalPenalty += calc.basicPenalty + calc.sefPenalty;
    });

    return {
      count: processedProperties.length,
      totalAmount,
      totalBasic,
      totalSef,
      totalPenalty
    };
  }, [processedProperties]);

  const handleGenerateNotice = () => {
    if (sortedProperties.length === 0) return;

    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    const noticesHtml = sortedProperties.map(prop => {
      const calc = prop.calculation;
      return `
        <div class="notice-page">
          <div class="header">
            <div class="lgu-name">REPUBLIC OF THE PHILIPPINES</div>
            <div class="province">PROVINCE OF ROMBLON</div>
            <div class="municipality">MUNICIPALITY OF CONCEPCION</div>
            <div class="office">OFFICE OF THE MUNICIPAL TREASURER</div>
          </div>
          
          <div class="date">${currentDate}</div>

          <div class="addressee">
            <strong>${prop.owner_name}</strong><br>
            ${prop.barangay}, Concepcion, Romblon
          </div>

          <div class="subject">
            <strong>SUBJECT: NOTICE OF DELINQUENCY</strong>
          </div>

          <div class="body">
            <p>Sir/Madam:</p>
            
            <p>Our records show that the Real Property Tax (Basic & SEF) due on your property/ies described below remains unpaid for the period indicated:</p>
            
            <table class="details-table">
              <thead>
                <tr>
                  <th>TD Number</th>
                  <th>Location</th>
                  <th>Classification</th>
                  <th>Period Covered</th>
                  <th>Total Amount Due</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${prop.td_number}</td>
                  <td>${prop.barangay}</td>
                  <td>${prop.classification_name || '-'}</td>
                  <td>${calc.startYear} - ${calc.endYear}</td>
                  <td>${formatCurrency(calc.grandTotal)}</td>
                </tr>
              </tbody>
            </table>

            <p>Please be informed that under the Local Government Code, failure to pay the said tax may subject your property to administrative or judicial remedies, including the sale of the property at public auction to satisfy the tax delinquency.</p>

            <p>We urge you to settle this obligation immediately at the Municipal Treasurer's Office to avoid further penalties and accumulation of interest.</p>

            <p>If payment has already been made, please disregard this notice and present your official receipt for updating of our records.</p>

            <p class="closing">Very truly yours,</p>

            <div class="signature">
              <img src="${window.location.origin}/templates/signature-menard.svg" alt="Municipal Treasurer Signature" style="height: 70px; width: auto;" />
            </div>
          </div>
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Notices of Delinquency</title>
          <style>
            @page {
              size: letter;
              margin: 0.75in;
            }
            body { 
              font-family: 'Times New Roman', serif; 
              font-size: 11.5pt; 
              line-height: 1.3; 
              color: #000;
              margin: 0;
            }
            .notice-page { 
              width: 100%;
              page-break-after: always; 
              position: relative;
            }
            .header { 
              text-align: center; 
              margin-bottom: 25px; 
              color: #003366;
            }
            .lgu-name { font-size: 12pt; }
            .province { font-size: 12pt; }
            .municipality { font-size: 12pt; font-weight: bold; }
            .office { font-size: 13pt; font-weight: bold; margin-top: 5px; }
            
            .date { text-align: right; margin-bottom: 25px; }
            
            .addressee { margin-bottom: 25px; text-transform: uppercase; }
            
            .subject { 
              text-align: center; 
              margin-bottom: 25px; 
              text-decoration: underline; 
              font-weight: bold;
              color: #990000;
            }
            
            .body p {
              text-align: justify;
              margin-bottom: 12px;
              text-indent: 2em;
            }
            .body p:first-of-type {
              text-indent: 0;
            }
            
            .details-table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 15px 0; 
              border: 1px solid #003366;
            }
            .details-table th, .details-table td { 
              border: 1px solid #003366; 
              padding: 6px; 
              text-align: center; 
              font-size: 11pt;
            }
            .details-table th { 
              background-color: #f0f4f8; 
              color: #003366;
              font-weight: bold;
            }
            
            .closing {
              margin-top: 30px;
              text-indent: 0 !important;
            }
            
            .signature { 
              margin-top: 15px; 
              text-align: left;
              width: 250px;
              padding-top: 0;
            }
            
            @media print {
              .notice-page { page-break-after: always; }
              body { -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${noticesHtml}
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleExport = () => {
    const csvHeader = [
      'Tax Dec. No.',
      'Owner Name',
      'Owner Address',
      'Barangay',
      'Kind',
      'Classification',
      'Assessed Value',
      'Annual Tax',
      'Start Year',
      'End Year',
      'Basic Tax',
      'Basic Penalty',
      'SEF Tax',
      'SEF Penalty',
      'Grand Total'
    ].join(',');

    const csvRows = sortedProperties.map(prop => {
      const calc = prop.calculation;
      return [
        `"${prop.td_number || ''}"`,
        `"${prop.owner_name || ''}"`,
        `"${prop.owner_address || ''}"`,
        `"${prop.barangay || ''}"`,
        `"${prop.kind_name || ''}"`,
        `"${prop.classification_name || ''}"`,
        prop.assessed_value || 0,
        calc.annualTax.toFixed(2),
        calc.startYear,
        calc.endYear,
        calc.basicTax.toFixed(2),
        calc.basicPenalty.toFixed(2),
        calc.sefTax.toFixed(2),
        calc.sefPenalty.toFixed(2),
        calc.grandTotal.toFixed(2)
      ].join(',');
    });

    const csvContent = [csvHeader, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const filename = statusFilter === 'delinquent' ? 'delinquency_report' : 'property_status_report';
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Delinquency Report');

    // Set page orientation to landscape
    worksheet.pageSetup.orientation = 'landscape';
    worksheet.pageSetup.paperSize = 9; // A4

    // 1. Title Section
    worksheet.mergeCells('A2:R2');
    const titleCell = worksheet.getCell('A2');
    
    let reportTitle = 'REAL PROPERTY STATUS REPORT';
    switch (statusFilter) {
      case 'delinquent':
        reportTitle = 'CERTIFIED LIST OF ALL REAL PROPERTY TAX DELINQUENCIES';
        break;
      case 'updated':
        reportTitle = 'LIST OF UPDATED REAL PROPERTY ASSESSMENTS';
        break;
      case 'due':
        reportTitle = 'LIST OF REAL PROPERTIES DUE FOR PAYMENT';
        break;
      case 'advance':
        reportTitle = 'LIST OF REAL PROPERTIES WITH ADVANCE PAYMENTS';
        break;
      case 'no_payment':
        reportTitle = 'LIST OF REAL PROPERTIES WITH NO PAYMENT HISTORY';
        break;
      default:
        reportTitle = 'REAL PROPERTY STATUS REPORT';
    }
    
    titleCell.value = reportTitle;
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    titleCell.font = { bold: true, size: 12, name: 'Calibri' };

    worksheet.mergeCells('A3:R3');
    const subTitleCell = worksheet.getCell('A3');
    const currentYear = new Date().getFullYear();
    // For delinquency report, it's usually "As of December 31, [Previous Year]" or current date?
    // User template says "As of December 31, 2025" (which is effectively end of last year for 2026 calc).
    // Let's use the actual reporting date or the "End Year" logic.
    // Since we use End Year = Current Year - 1 for delinquent, let's say "As of December 31, [EndYear]"
    const reportYear = statusFilter === 'delinquent' ? currentYear - 1 : currentYear;
    subTitleCell.value = `As of December 31, ${reportYear}`;
    subTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    subTitleCell.font = { size: 11, name: 'Calibri' };

    // 2. Header Rows Setup (Row 5, 6, 7)
    
    // Row 5 headers (Top level)
    worksheet.getCell('A5').value = 'NO.';
    worksheet.getCell('B5').value = 'Tax Dec.No./AR';
    worksheet.getCell('C5').value = 'Name of Owner';
    worksheet.getCell('D5').value = 'Mailing Address';
    // Barangay removed
    worksheet.getCell('E5').value = 'Classification of Property';
    
    worksheet.getCell('F5').value = 'ASSESSED VALUE';
    worksheet.getCell('I5').value = 'Annual Tax';
    worksheet.getCell('J5').value = 'Year';
    
    worksheet.getCell('K5').value = 'Basic';
    worksheet.getCell('N5').value = 'SEF';
    
    worksheet.getCell('Q5').value = 'Grand Total';
    worksheet.getCell('R5').value = 'Remarks';

    // Merges for Row 5-6 (Vertical)
    worksheet.mergeCells('A5:A6');
    worksheet.mergeCells('B5:B6');
    worksheet.mergeCells('C5:C6');
    worksheet.mergeCells('D5:D6');
    worksheet.mergeCells('E5:E6'); // Classification
    worksheet.mergeCells('I5:I6'); // Annual Tax
    worksheet.mergeCells('J5:J6'); // Year
    worksheet.mergeCells('Q5:Q6'); // Grand Total
    worksheet.mergeCells('R5:R6'); // Remarks

    // Merges for Row 5 (Horizontal)
    worksheet.mergeCells('F5:H5'); // Assessed Value (Land, Imp, Mach)
    worksheet.mergeCells('K5:M5'); // Basic
    worksheet.mergeCells('N5:P5'); // SEF

    // Row 6 headers (Sub level)
    worksheet.getCell('F6').value = 'Land';
    worksheet.getCell('G6').value = 'Imp.';
    worksheet.getCell('H6').value = 'Mach';
    
    worksheet.getCell('K6').value = 'Tax';
    worksheet.getCell('L6').value = 'Penalty';
    worksheet.getCell('M6').value = 'Subtotal (11+12)';
    
    worksheet.getCell('N6').value = 'Tax';
    worksheet.getCell('O6').value = 'Penalty';
    worksheet.getCell('P6').value = 'Subtotal (14+15)';

    // Row 7 headers (Column Numbers)
    const colNums = ['(1)', '(2)', '(3)', '(4)', '(5)', '(6)', '(7)', '(8)', '(9)', '(10)', '(11)', '(12)', '(13)', '(14)', '(15)', '(16)', '(17)', '(18)'];
    colNums.forEach((val, idx) => {
        if (val) {
            const cell = worksheet.getCell(7, idx + 1);
            cell.value = val;
            cell.alignment = { horizontal: 'center' };
        }
    });

    // Styling Headers
    // Apply style to A5:R7
    for (let r = 5; r <= 7; r++) {
        for (let c = 1; c <= 18; c++) {
            const cell = worksheet.getCell(r, c);
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            if (r <= 6) {
                cell.font = { bold: true, name: 'Calibri', size: 10 };
            } else {
                cell.font = { italic: true, name: 'Calibri', size: 9 };
            }
        }
    }

    // 3. Data Rows
    let currentRow = 8;
    
    // Initialize accumulators for totals
    let totalAV = 0;
    let totalAnnualTax = 0;
    let totalBasicTax = 0;
    let totalBasicPenalty = 0;
    let totalBasicSubtotal = 0;
    let totalSefTax = 0;
    let totalSefPenalty = 0;
    let totalSefSubtotal = 0;
    let totalGrandTotal = 0;

    sortedProperties.forEach((prop, index) => {
        const calc = prop.calculation;
        const row = worksheet.getRow(currentRow);
        
        // (1) NO.
        row.getCell(1).value = index + 1;
        
        // (2) Tax Dec
        row.getCell(2).value = prop.td_number || prop.arpn || '';
        
        // (3) Owner
        row.getCell(3).value = prop.owner_name || '';
        
        // (4) Address
        row.getCell(4).value = prop.owner_address || '';
        
        // (5) Classification / Kind
        // Template shows "Building/Residential"
        const kind = prop.kind_name || '';
        const classification = prop.classification_name || '';
        row.getCell(5).value = `${kind}/${classification}`;

        // (6)-(8) Assessed Value Breakdown
        // User request: Assessed value in Assessed Value column (first one), Imp and Mach blank.
        const av = parseFloat(prop.assessed_value || 0);
        totalAV += av;
        
        row.getCell(6).value = av; // Land (Primary AV Column)
        row.getCell(7).value = ''; // Imp (Blank)
        row.getCell(8).value = ''; // Mach (Blank)

        // (9) Annual Tax
        row.getCell(9).value = calc.annualTax;
        totalAnnualTax += calc.annualTax;

        // (10) Year
        // User request: Start year only
        row.getCell(10).value = calc.startYear;

        // (11)-(13) Basic
        row.getCell(11).value = calc.basicTax;      // Tax
        row.getCell(12).value = calc.basicPenalty;  // Penalty
        const basicSub = calc.basicTax + calc.basicPenalty;
        row.getCell(13).value = basicSub; // Subtotal
        
        totalBasicTax += calc.basicTax;
        totalBasicPenalty += calc.basicPenalty;
        totalBasicSubtotal += basicSub;

        // (14)-(16) SEF
        row.getCell(14).value = calc.sefTax;        // Tax
        row.getCell(15).value = calc.sefPenalty;    // Penalty
        const sefSub = calc.sefTax + calc.sefPenalty;
        row.getCell(16).value = sefSub; // Subtotal
        
        totalSefTax += calc.sefTax;
        totalSefPenalty += calc.sefPenalty;
        totalSefSubtotal += sefSub;

        // (17) Grand Total
        row.getCell(17).value = calc.grandTotal;
        totalGrandTotal += calc.grandTotal;

        // (18) Remarks
        row.getCell(18).value = '';

        // Styling Data Row
        for (let c = 1; c <= 18; c++) {
            const cell = row.getCell(c);
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
            cell.font = { name: 'Calibri', size: 10 };
            
            // Alignment
            if (c === 1 || c === 10) cell.alignment = { horizontal: 'center' }; // No, Year
            else if (c >= 6 && c !== 10 && c !== 18) {
                cell.alignment = { horizontal: 'right' };
                cell.numFmt = '#,##0.00'; // Currency format
            } else {
                cell.alignment = { horizontal: 'left', wrapText: true };
            }
        }

        currentRow++;
    });

    // 4. Totals Row
    const totalRow = worksheet.getRow(currentRow);
    totalRow.getCell(5).value = 'TOTAL';
    totalRow.getCell(6).value = totalAV;
    totalRow.getCell(9).value = totalAnnualTax;
    totalRow.getCell(11).value = totalBasicTax;
    totalRow.getCell(12).value = totalBasicPenalty;
    totalRow.getCell(13).value = totalBasicSubtotal;
    totalRow.getCell(14).value = totalSefTax;
    totalRow.getCell(15).value = totalSefPenalty;
    totalRow.getCell(16).value = totalSefSubtotal;
    totalRow.getCell(17).value = totalGrandTotal;

    // Style Total Row
    for (let c = 1; c <= 18; c++) {
        const cell = totalRow.getCell(c);
        cell.border = {
            top: { style: 'medium' }, // Thicker top border for separation
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
        cell.font = { name: 'Calibri', size: 10, bold: true };
        
        if (c === 5) {
             cell.alignment = { horizontal: 'right' };
        } else if (c >= 6 && c !== 10 && c !== 18 && cell.value) {
            cell.alignment = { horizontal: 'right' };
            cell.numFmt = '#,##0.00';
        } else {
            cell.alignment = { horizontal: 'center' };
        }
    }

    // Column Widths
    worksheet.getColumn(1).width = 5;  // NO.
    worksheet.getColumn(2).width = 15; // TD
    worksheet.getColumn(3).width = 25; // Owner
    worksheet.getColumn(4).width = 25; // Address
    worksheet.getColumn(5).width = 20; // Class
    worksheet.getColumn(6).width = 12; // Land
    worksheet.getColumn(7).width = 12; // Imp
    worksheet.getColumn(8).width = 12; // Mach
    worksheet.getColumn(9).width = 12; // Annual Tax
    worksheet.getColumn(10).width = 10; // Year
    worksheet.getColumn(11).width = 12; // Basic Tax
    worksheet.getColumn(12).width = 12; // Basic Penalty
    worksheet.getColumn(13).width = 14; // Basic Sub
    worksheet.getColumn(14).width = 12; // SEF Tax
    worksheet.getColumn(15).width = 12; // SEF Penalty
    worksheet.getColumn(16).width = 14; // SEF Sub
    worksheet.getColumn(17).width = 15; // Total
    worksheet.getColumn(18).width = 15; // Remarks

    // Generate buffer
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    let filenamePrefix = 'Property_Status_Report';
    switch (statusFilter) {
      case 'delinquent':
        filenamePrefix = 'Delinquency_Report';
        break;
      case 'updated':
        filenamePrefix = 'Updated_Assessments_Report';
        break;
      case 'due':
        filenamePrefix = 'For_Payment_Report';
        break;
      case 'advance':
        filenamePrefix = 'Advance_Payment_Report';
        break;
      case 'no_payment':
        filenamePrefix = 'No_Payment_History_Report';
        break;
      default:
        filenamePrefix = 'Property_Status_Report';
    }

    const filename = `${filenamePrefix}_${reportYear}_${new Date().getTime()}.xlsx`;
    saveAs(blob, filename);
  };

  const handleExportExcelByBarangay = async () => {
    const workbook = new ExcelJS.Workbook();

    const grouped = {};
    sortedProperties.forEach((prop) => {
      const key = prop.barangay || 'Unspecified';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(prop);
    });

    const barangayNames = Object.keys(grouped).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );

    barangayNames.forEach((barangay) => {
      const sheetName = barangay.length > 28 ? barangay.slice(0, 28) : barangay || 'Sheet';
      const worksheet = workbook.addWorksheet(sheetName || 'Sheet');

      worksheet.pageSetup.orientation = 'landscape';
      worksheet.pageSetup.paperSize = 9;

      worksheet.mergeCells('A2:R2');
      const titleCell = worksheet.getCell('A2');

      let reportTitle = 'REAL PROPERTY STATUS REPORT';
      switch (statusFilter) {
        case 'delinquent':
          reportTitle = 'CERTIFIED LIST OF ALL REAL PROPERTY TAX DELINQUENCIES';
          break;
        case 'updated':
          reportTitle = 'LIST OF UPDATED REAL PROPERTY ASSESSMENTS';
          break;
        case 'due':
          reportTitle = 'LIST OF REAL PROPERTIES DUE FOR PAYMENT';
          break;
        case 'advance':
          reportTitle = 'LIST OF REAL PROPERTIES WITH ADVANCE PAYMENTS';
          break;
        case 'no_payment':
          reportTitle = 'LIST OF REAL PROPERTIES WITH NO PAYMENT HISTORY';
          break;
        default:
          reportTitle = 'REAL PROPERTY STATUS REPORT';
      }

      titleCell.value = reportTitle;
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      titleCell.font = { bold: true, size: 12, name: 'Calibri' };

      worksheet.mergeCells('A3:R3');
      const subTitleCell = worksheet.getCell('A3');
      subTitleCell.value = `As of December 31, ${statusFilter === 'delinquent' ? new Date().getFullYear() - 1 : new Date().getFullYear()} • ${barangay}`;
      subTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      subTitleCell.font = { size: 11, name: 'Calibri' };

      worksheet.getCell('A5').value = 'NO.';
      worksheet.getCell('B5').value = 'Tax Dec.No./AR';
      worksheet.getCell('C5').value = 'Name of Owner';
      worksheet.getCell('D5').value = 'Mailing Address';
      worksheet.getCell('E5').value = 'Classification of Property';
      worksheet.getCell('F5').value = 'ASSESSED VALUE';
      worksheet.getCell('I5').value = 'Annual Tax';
      worksheet.getCell('J5').value = 'Year';
      worksheet.getCell('K5').value = 'Basic';
      worksheet.getCell('N5').value = 'SEF';
      worksheet.getCell('Q5').value = 'Grand Total';
      worksheet.getCell('R5').value = 'Remarks';

      worksheet.mergeCells('A5:A6');
      worksheet.mergeCells('B5:B6');
      worksheet.mergeCells('C5:C6');
      worksheet.mergeCells('D5:D6');
      worksheet.mergeCells('E5:E6');
      worksheet.mergeCells('I5:I6');
      worksheet.mergeCells('J5:J6');
      worksheet.mergeCells('Q5:Q6');
      worksheet.mergeCells('R5:R6');

      worksheet.mergeCells('F5:H5');
      worksheet.mergeCells('K5:M5');
      worksheet.mergeCells('N5:P5');

      worksheet.getCell('F6').value = 'Land';
      worksheet.getCell('G6').value = 'Imp.';
      worksheet.getCell('H6').value = 'Mach';

      worksheet.getCell('K6').value = 'Tax';
      worksheet.getCell('L6').value = 'Penalty';
      worksheet.getCell('M6').value = 'Subtotal (11+12)';

      worksheet.getCell('N6').value = 'Tax';
      worksheet.getCell('O6').value = 'Penalty';
      worksheet.getCell('P6').value = 'Subtotal (14+15)';

      const colNums = ['(1)', '(2)', '(3)', '(4)', '(5)', '(6)', '(7)', '(8)', '(9)', '(10)', '(11)', '(12)', '(13)', '(14)', '(15)', '(16)', '(17)', '(18)'];
      colNums.forEach((val, idx) => {
        if (val) {
          const cell = worksheet.getCell(7, idx + 1);
          cell.value = val;
          cell.alignment = { horizontal: 'center' };
        }
      });

      for (let r = 5; r <= 7; r++) {
        for (let c = 1; c <= 18; c++) {
          const cell = worksheet.getCell(r, c);
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          if (r <= 6) {
            cell.font = { bold: true, name: 'Calibri', size: 10 };
          } else {
            cell.font = { italic: true, name: 'Calibri', size: 9 };
          }
        }
      }

      let currentRow = 8;

      let totalAV = 0;
      let totalAnnualTax = 0;
      let totalBasicTax = 0;
      let totalBasicPenalty = 0;
      let totalBasicSubtotal = 0;
      let totalSefTax = 0;
      let totalSefPenalty = 0;
      let totalSefSubtotal = 0;
      let totalGrandTotal = 0;

      grouped[barangay].forEach((prop, index) => {
        const calc = prop.calculation;
        const row = worksheet.getRow(currentRow);

        row.getCell(1).value = index + 1;
        row.getCell(2).value = prop.td_number || prop.arpn || '';
        row.getCell(3).value = prop.owner_name || '';
        row.getCell(4).value = prop.owner_address || '';

        const kind = prop.kind_name || '';
        const classification = prop.classification_name || '';
        row.getCell(5).value = `${kind}/${classification}`;

        const av = parseFloat(prop.assessed_value || 0);
        totalAV += av;

        row.getCell(6).value = av;
        row.getCell(7).value = '';
        row.getCell(8).value = '';

        row.getCell(9).value = calc.annualTax;
        totalAnnualTax += calc.annualTax;

        row.getCell(10).value = calc.startYear;

        row.getCell(11).value = calc.basicTax;
        row.getCell(12).value = calc.basicPenalty;
        const basicSub = calc.basicTax + calc.basicPenalty;
        row.getCell(13).value = basicSub;

        totalBasicTax += calc.basicTax;
        totalBasicPenalty += calc.basicPenalty;
        totalBasicSubtotal += basicSub;

        row.getCell(14).value = calc.sefTax;
        row.getCell(15).value = calc.sefPenalty;
        const sefSub = calc.sefTax + calc.sefPenalty;
        row.getCell(16).value = sefSub;

        totalSefTax += calc.sefTax;
        totalSefPenalty += calc.sefPenalty;
        totalSefSubtotal += sefSub;

        row.getCell(17).value = calc.grandTotal;
        totalGrandTotal += calc.grandTotal;

        row.getCell(18).value = '';

        for (let c = 1; c <= 18; c++) {
          const cell = row.getCell(c);
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.font = { name: 'Calibri', size: 10 };

          if (c === 1 || c === 10) cell.alignment = { horizontal: 'center' };
          else if (c >= 6 && c !== 10 && c !== 18) {
            cell.alignment = { horizontal: 'right' };
            cell.numFmt = '#,##0.00';
          } else {
            cell.alignment = { horizontal: 'left', wrapText: true };
          }
        }

        currentRow++;
      });

      const totalRow = worksheet.getRow(currentRow);
      totalRow.getCell(5).value = 'TOTAL';
      totalRow.getCell(6).value = totalAV;
      totalRow.getCell(9).value = totalAnnualTax;
      totalRow.getCell(11).value = totalBasicTax;
      totalRow.getCell(12).value = totalBasicPenalty;
      totalRow.getCell(13).value = totalBasicSubtotal;
      totalRow.getCell(14).value = totalSefTax;
      totalRow.getCell(15).value = totalSefPenalty;
      totalRow.getCell(16).value = totalSefSubtotal;
      totalRow.getCell(17).value = totalGrandTotal;

      for (let c = 1; c <= 18; c++) {
        const cell = totalRow.getCell(c);
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.font = { name: 'Calibri', size: 10, bold: true };

        if (c === 5) {
          cell.alignment = { horizontal: 'right' };
        } else if (c >= 6 && c !== 10 && c !== 18 && cell.value) {
          cell.alignment = { horizontal: 'right' };
          cell.numFmt = '#,##0.00';
        } else {
          cell.alignment = { horizontal: 'center' };
        }
      }

      worksheet.getColumn(1).width = 5;
      worksheet.getColumn(2).width = 15;
      worksheet.getColumn(3).width = 25;
      worksheet.getColumn(4).width = 25;
      worksheet.getColumn(5).width = 20;
      worksheet.getColumn(6).width = 12;
      worksheet.getColumn(7).width = 12;
      worksheet.getColumn(8).width = 12;
      worksheet.getColumn(9).width = 12;
      worksheet.getColumn(10).width = 10;
      worksheet.getColumn(11).width = 12;
      worksheet.getColumn(12).width = 12;
      worksheet.getColumn(13).width = 14;
      worksheet.getColumn(14).width = 12;
      worksheet.getColumn(15).width = 12;
      worksheet.getColumn(16).width = 14;
      worksheet.getColumn(17).width = 15;
      worksheet.getColumn(18).width = 15;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    let filenamePrefix = 'Property_Status_Report_By_Barangay';
    switch (statusFilter) {
      case 'delinquent':
        filenamePrefix = 'Delinquency_Report_By_Barangay';
        break;
      case 'updated':
        filenamePrefix = 'Updated_Assessments_Report_By_Barangay';
        break;
      case 'due':
        filenamePrefix = 'For_Payment_Report_By_Barangay';
        break;
      case 'advance':
        filenamePrefix = 'Advance_Payment_Report_By_Barangay';
        break;
      case 'no_payment':
        filenamePrefix = 'No_Payment_History_Report_By_Barangay';
        break;
      default:
        filenamePrefix = 'Property_Status_Report_By_Barangay';
    }

    const filename = `${filenamePrefix}_${new Date().getFullYear()}_${new Date().getTime()}.xlsx`;
    saveAs(blob, filename);
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 8 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.875rem' }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/portal/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Portal
            </Link>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/portal/property-tax/dashboard')}
            >
              RPT System
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 500 }}>
              {statusFilter === 'delinquent' ? 'Delinquency Report' : 'Property Status Report'}
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* Header Section */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              color: '#1e293b', 
              letterSpacing: '-0.02em',
              background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}>
              {statusFilter === 'delinquent' ? 'Delinquency Report' : 
               statusFilter === 'updated' ? 'Updated Properties Report' :
               statusFilter === 'due' ? 'For Payment Properties Report' :
               statusFilter === 'advance' ? 'Advance Payment Report' :
               statusFilter === 'no_payment' ? 'No Payment History Report' :
               'Property Status Report'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px', lineHeight: 1.6 }}>
              {statusFilter === 'delinquent' 
                ? 'Comprehensive list of real property tax accounts with outstanding balances. Data is generated based on current assessment records and payment history.'
                : 'List of properties filtered by payment status. Data is generated based on current assessment records and payment history.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            {statusFilter === 'delinquent' && (
              <Button
                variant="contained"
                startIcon={<MailIcon />}
                onClick={handleGenerateNotice}
                disabled={loading || sortedProperties.length === 0}
                sx={{ 
                  bgcolor: '#3b82f6', 
                  '&:hover': { bgcolor: '#2563eb' },
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)'
                }}
              >
                Generate Notice
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={loading || properties.length === 0}
              sx={{ 
                bgcolor: '#1e293b', 
                '&:hover': { bgcolor: '#334155' },
                px: 3,
                py: 1.2,
                borderRadius: 2,
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)'
              }}
            >
              Print Report
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Summary Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)', 
              height: '100%',
              borderTop: '4px solid #3b82f6'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: '#eff6ff', color: '#3b82f6', mr: 2 }}>
                    <AssessmentIcon />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    TOTAL PROPERTIES
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="800" color="#1e293b">
                  {loading ? <CircularProgress size={24} /> : summaryStats.count.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statusFilter === 'delinquent' ? 'Delinquent accounts found' : 'Properties found'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)', 
              height: '100%',
              borderTop: '4px solid #ef4444'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: '#fef2f2', color: '#ef4444', mr: 2 }}>
                    <MonetizationOnIcon />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    TOTAL AMOUNT
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="800" color="#1e293b">
                  {loading ? <CircularProgress size={24} /> : formatCurrency(summaryStats.totalAmount)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total outstanding balance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)', 
              height: '100%',
              borderTop: '4px solid #22c55e'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: '#f0fdf4', color: '#22c55e', mr: 2 }}>
                    <AccountBalanceIcon />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    TOTAL BASIC TAX
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="800" color="#1e293b">
                  {loading ? <CircularProgress size={24} /> : formatCurrency(summaryStats.totalBasic)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Principal basic tax due
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)', 
              height: '100%',
              borderTop: '4px solid #f97316'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: '#fff7ed', color: '#f97316', mr: 2 }}>
                    <AccountBalanceIcon />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    TOTAL SEF
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="800" color="#1e293b">
                  {loading ? <CircularProgress size={24} /> : formatCurrency(summaryStats.totalSef)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Special Education Fund due
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters & Actions */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', overflow: 'visible' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  placeholder="Search..."
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ bgcolor: '#f8fafc' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl size="small" fullWidth sx={{ bgcolor: '#f8fafc' }}>
                  <InputLabel>Filter by Barangay</InputLabel>
                  <Select
                    value={barangayFilter}
                    label="Filter by Barangay"
                    onChange={(e) => setBarangayFilter(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterListIcon color="action" fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value=""><em>All Barangays</em></MenuItem>
                    {barangays.map((b) => (
                      <MenuItem key={b} value={b}>{b}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl size="small" fullWidth sx={{ bgcolor: '#f8fafc' }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="updated">Updated</MenuItem>
                    <MenuItem value="due">For Payment</MenuItem>
                    <MenuItem value="delinquent">Delinquent</MenuItem>
                    <MenuItem value="advance">Advance</MenuItem>
                    <MenuItem value="no_payment">No Payment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1}>
                  <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={sortedProperties.length === 0}
                sx={{ 
                      flex: 1,
                      height: '40px',
                      color: '#64748b',
                      borderColor: '#cbd5e1',
                      '&:hover': {
                        borderColor: '#94a3b8',
                        bgcolor: '#f8fafc',
                        color: '#475569'
                      }
                    }}
                  >
                    CSV
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TableChartIcon />}
                    onClick={handleExportExcel}
                    disabled={sortedProperties.length === 0}
                    sx={{ 
                      height: '40px',
                      whiteSpace: 'nowrap',
                      color: '#059669',
                      borderColor: '#6ee7b7',
                      '&:hover': {
                        borderColor: '#059669',
                        bgcolor: '#ecfdf5',
                        color: '#047857'
                      }
                    }}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TableChartIcon />}
                    onClick={handleExportExcelByBarangay}
                    disabled={sortedProperties.length === 0}
                    sx={{ 
                      height: '40px',
                      whiteSpace: 'nowrap',
                      color: '#0f766e',
                      borderColor: '#67e8f9',
                      '&:hover': {
                        borderColor: '#0f766e',
                        bgcolor: '#ecfeff',
                        color: '#0e7490'
                      }
                    }}
                  >
                    By Barangay
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Main Table */}
        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
           {loading ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
               <CircularProgress />
             </Box>
           ) : (
             <>
               <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
                 <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                   <TableHead>
                     <TableRow>
                       <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0', width: '120px' }}>
                         <TableSortLabel
                           active={sortConfig.key === 'td_number'}
                           direction={sortConfig.key === 'td_number' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('td_number')}
                         >
                           Tax Dec. No.
                         </TableSortLabel>
                       </TableCell>
                       <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0' }}>
                         <TableSortLabel
                           active={sortConfig.key === 'owner_name'}
                           direction={sortConfig.key === 'owner_name' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('owner_name')}
                         >
                           Property Owner
                         </TableSortLabel>
                       </TableCell>
                       <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0' }}>
                         <TableSortLabel
                           active={sortConfig.key === 'barangay'}
                           direction={sortConfig.key === 'barangay' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('barangay')}
                         >
                           Location
                         </TableSortLabel>
                       </TableCell>
                       <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0' }}>
                         <TableSortLabel
                           active={sortConfig.key === 'kind_name'}
                           direction={sortConfig.key === 'kind_name' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('kind_name')}
                         >
                           Classification
                         </TableSortLabel>
                       </TableCell>
                       <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0' }} align="right">
                         <TableSortLabel
                           active={sortConfig.key === 'assessed_value'}
                           direction={sortConfig.key === 'assessed_value' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('assessed_value')}
                         >
                           Assessed Value
                         </TableSortLabel>
                       </TableCell>
                       <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0' }} align="right">
                         <TableSortLabel
                           active={sortConfig.key === 'annualTax'}
                           direction={sortConfig.key === 'annualTax' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('annualTax')}
                         >
                           Annual Tax
                         </TableSortLabel>
                       </TableCell>
                       <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0' }} align="center">
                         <TableSortLabel
                           active={sortConfig.key === 'startYear'}
                           direction={sortConfig.key === 'startYear' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('startYear')}
                         >
                           Start Year
                         </TableSortLabel>
                       </TableCell>
                       <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0' }} align="center">
                         <TableSortLabel
                           active={sortConfig.key === 'endYear'}
                           direction={sortConfig.key === 'endYear' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('endYear')}
                         >
                           End Year
                         </TableSortLabel>
                       </TableCell>
                       <TableCell colSpan={3} sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155', textAlign: 'center', borderLeft: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Basic Tax</TableCell>
                       <TableCell colSpan={3} sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155', textAlign: 'center', borderLeft: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>SEF</TableCell>
                       <TableCell rowSpan={2} sx={{ fontWeight: 800, bgcolor: '#eff6ff', color: '#1d4ed8', borderBottom: '2px solid #bfdbfe' }} align="right">
                         <TableSortLabel
                           active={sortConfig.key === 'grandTotal'}
                           direction={sortConfig.key === 'grandTotal' ? sortConfig.direction : 'asc'}
                           onClick={() => handleSort('grandTotal')}
                         >
                           Grand Total
                         </TableSortLabel>
                       </TableCell>
                     </TableRow>
                     <TableRow>
                       <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', fontSize: '0.75rem', borderLeft: '1px solid #e2e8f0', color: '#64748b' }} align="right">Tax</TableCell>
                       <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', fontSize: '0.75rem', color: '#64748b' }} align="right">Penalty</TableCell>
                       <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', fontSize: '0.75rem', color: '#64748b' }} align="right">Subtotal</TableCell>
                       <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', fontSize: '0.75rem', borderLeft: '1px solid #e2e8f0', color: '#64748b' }} align="right">Tax</TableCell>
                       <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', fontSize: '0.75rem', color: '#64748b' }} align="right">Penalty</TableCell>
                       <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', fontSize: '0.75rem', color: '#64748b' }} align="right">Subtotal</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                    {sortedProperties
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((prop, index) => {
                      const calc = prop.calculation;
                      return (
                        <TableRow 
                          key={prop.property_id || index} 
                          hover
                          onClick={() => handleRowClick(prop, calc)}
                          sx={{ 
                            cursor: 'pointer', 
                            transition: 'all 0.1s',
                            '&:hover': { bgcolor: '#f1f5f9 !important' },
                            '&:nth-of-type(odd)': { bgcolor: '#ffffff' },
                            '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
                            '&:last-child td, &:last-child th': { border: 0 }
                          }}
                        >
                           <TableCell sx={{ fontWeight: 600, color: '#334155' }}>
                              <Chip label={prop.td_number || prop.arpn || 'N/A'} size="small" sx={{ bgcolor: '#e2e8f0', fontWeight: 600, borderRadius: 1 }} />
                           </TableCell>
                           <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{prop.owner_name || 'Unknown'}</TableCell>
                           <TableCell>{prop.barangay || 'N/A'}</TableCell>
                           <TableCell>
                             <Box>
                               <Typography variant="body2" sx={{ fontWeight: 600 }}>{prop.kind_name}</Typography>
                               <Typography variant="caption" color="text.secondary">{prop.classification_name}</Typography>
                             </Box>
                           </TableCell>
                           <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{formatCurrency(prop.assessed_value)}</TableCell>
                           <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{formatCurrency(calc.annualTax)}</TableCell>
                           <TableCell align="center" sx={{ color: 'text.secondary' }}>
                              <Chip label={calc.startYear} size="small" variant="outlined" color="warning" sx={{ fontWeight: 600, height: 24 }} />
                           </TableCell>
                           <TableCell align="center" sx={{ color: 'text.secondary' }}>
                              <Chip label={calc.endYear} size="small" variant="outlined" color="info" sx={{ fontWeight: 600, height: 24 }} />
                           </TableCell>
                           
                           {/* Basic */}
                           <TableCell align="right" sx={{ color: 'text.secondary', borderLeft: '1px solid #f1f5f9', fontFamily: 'monospace' }}>{formatCurrency(calc.basicTax)}</TableCell>
                           <TableCell align="right" sx={{ color: '#ef4444', fontFamily: 'monospace' }}>{formatCurrency(calc.basicPenalty)}</TableCell>
                           <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontFamily: 'monospace' }}>{formatCurrency(calc.basicSubtotal)}</TableCell>
                           
                           {/* SEF */}
                           <TableCell align="right" sx={{ color: 'text.secondary', borderLeft: '1px solid #f1f5f9', fontFamily: 'monospace' }}>{formatCurrency(calc.sefTax)}</TableCell>
                           <TableCell align="right" sx={{ color: '#ef4444', fontFamily: 'monospace' }}>{formatCurrency(calc.sefPenalty)}</TableCell>
                           <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontFamily: 'monospace' }}>{formatCurrency(calc.sefSubtotal)}</TableCell>
                           
                           <TableCell align="right" sx={{ fontWeight: 800, bgcolor: '#eff6ff', color: '#1d4ed8', fontSize: '0.95rem', fontFamily: 'monospace' }}>
                             {formatCurrency(calc.grandTotal)}
                           </TableCell>
                         </TableRow>
                       );
                     })}
                     {sortedProperties.length === 0 && (
                       <TableRow>
                         <TableCell colSpan={15} align="center" sx={{ py: 8 }}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                             <SearchIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                             <Typography variant="h6" color="text.secondary" gutterBottom>
                               No delinquent properties found
                             </Typography>
                             <Typography variant="body2" color="text.secondary">
                               Try adjusting your search or filter criteria.
                             </Typography>
                           </Box>
                         </TableCell>
                       </TableRow>
                     )}
                   </TableBody>
                 </Table>
               </TableContainer>
               <Divider />
               <TablePagination
                rowsPerPageOptions={[20, 50, 100]}
                component="div"
                count={sortedProperties.length}
                rowsPerPage={rowsPerPage}
                 page={page}
                 onPageChange={handleChangePage}
                 onRowsPerPageChange={handleChangeRowsPerPage}
               />
             </>
           )}
        </Card>

        {/* Breakdown Modal */}
        <Dialog
          open={!!selectedProperty}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2, boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)' }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', p: 3 }}>
            <Box>
              <Typography variant="h6" component="div" fontWeight="800" color="#1e293b">
                Delinquency Breakdown
              </Typography>
              {selectedProperty && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedProperty.owner_name} • <span style={{ fontFamily: 'monospace' }}>{selectedProperty.td_number || selectedProperty.arpn}</span>
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseModal} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {breakdownData && (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Year</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">Basic Tax</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">Basic Penalty</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">SEF Tax</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">SEF Penalty</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1e293b' }} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breakdownData.breakdown.map((row) => (
                      <TableRow key={row.year} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">{row.year}</Typography>
                          {row.isAmnesty && (
                            <Chip 
                              label={row.penaltyPercent === 0 ? "Amnesty" : `Penalty (${row.penaltyPercent}%)`} 
                              size="small" 
                              color={row.penaltyPercent === 0 ? "success" : "error"} 
                              sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }} 
                            />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{formatCurrency(row.basicTax)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#ef4444' }}>{formatCurrency(row.basicPenalty)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{formatCurrency(row.sefTax)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#ef4444' }}>{formatCurrency(row.sefPenalty)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(row.total)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Summary Footer in Modal */}
                    <TableRow sx={{ bgcolor: '#eff6ff' }}>
                       <TableCell sx={{ fontWeight: 800, color: '#1e293b' }}>TOTAL</TableCell>
                       <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(breakdownData.basicTax)}</TableCell>
                       <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#ef4444' }}>{formatCurrency(breakdownData.basicPenalty)}</TableCell>
                       <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(breakdownData.sefTax)}</TableCell>
                       <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#ef4444' }}>{formatCurrency(breakdownData.sefPenalty)}</TableCell>
                       <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 800, color: '#1d4ed8', fontSize: '1rem' }}>{formatCurrency(breakdownData.grandTotal)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseModal} variant="outlined" sx={{ color: '#475569', borderColor: '#cbd5e1' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Footer */}
        <Box sx={{ mt: 8, borderTop: '1px solid #e2e8f0', pt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Real Property Tax System • Official Delinquency Report
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Generated on {new Date().toLocaleDateString()} • Confidential Document
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ReportsPage;
